import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          <Card className="border-2 border-muted shadow-lg">
            <CardContent className="p-8">
              <h1 className="text-3xl font-bold mb-6 text-center">Privacy Policy</h1>
              <p className="text-sm text-muted-foreground mb-6 text-center">Effective Date: June 7, 2025</p>

              <div className="prose prose-invert max-w-none">
                <p className="mb-6">
                  At Legalish ("we", "our", or "us"), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website <a href="https://legalish.site" className="text-primary hover:underline">https://legalish.site</a>, browser extension, and related services (collectively, the "Services").
                </p>
                
                <p className="mb-6">
                  By accessing or using our Services, you agree to the collection and use of your information in accordance with this Privacy Policy.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4">Information We Collect</h2>
                
                <h3 className="text-xl font-medium mt-6 mb-3">a. Information You Provide</h3>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Uploaded documents:</strong> When you submit legal or terms-based documents for analysis.</li>
                  <li><strong>Account details:</strong> If you create an account (e.g., email, name).</li>
                  <li><strong>Payment information:</strong> Collected and processed securely via third-party providers (e.g., Stripe, RevenueCat).</li>
                </ul>

                <h3 className="text-xl font-medium mt-6 mb-3">b. Automatically Collected Data</h3>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li><strong>Usage data:</strong> Browser type, pages visited, session duration.</li>
                  <li><strong>Device information:</strong> IP address, device ID, OS type.</li>
                  <li><strong>Cookies:</strong> To improve functionality and track usage behavior.</li>
                </ul>

                <h2 className="text-2xl font-semibold mt-8 mb-4">How We Use Your Information</h2>
                <p className="mb-4">We use your information to:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Analyze and summarize legal documents using AI models.</li>
                  <li>Improve and personalize the user experience.</li>
                  <li>Communicate with you about updates, features, and support.</li>
                  <li>Provide customer service and respond to inquiries.</li>
                  <li>Monitor system performance and prevent abuse.</li>
                </ul>
                <p className="mb-6"><strong>We do not sell or rent your personal information.</strong></p>

                <h2 className="text-2xl font-semibold mt-8 mb-4">AI-Generated Content Disclaimer</h2>
                <p className="mb-4">
                  The summaries and outputs provided by Legalish are generated by a large language model trained on diverse legal and public datasets. These summaries are not guaranteed to be fully accurate or legally comprehensive and should not be used as a substitute for professional legal advice.
                </p>
                <p className="mb-6">
                  Our models are continuously updated and retrained to improve performance over time. While accuracy improves with new training data, errors and misinterpretations may still occur. Users should exercise discretion and consult a legal professional when necessary.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4">Sharing and Disclosure</h2>
                <p className="mb-4">We may share information:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>With third-party vendors for payment processing, hosting, analytics, and voice services.</li>
                  <li>In response to legal obligations or to protect rights, safety, and property.</li>
                  <li>With your explicit consent for specific features or integrations.</li>
                </ul>
                <p className="mb-6">All shared data is transferred securely and in accordance with applicable laws.</p>

                <h2 className="text-2xl font-semibold mt-8 mb-4">Data Retention</h2>
                <p className="mb-6">
                  We retain user data only as long as necessary to fulfill the purposes described in this policy or as required by law. You may request deletion of your documents or account at any time.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4">Your Rights and Choices</h2>
                <p className="mb-4">You have the right to:</p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Access and review the data we hold about you.</li>
                  <li>Correct inaccuracies in your personal data.</li>
                  <li>Request deletion of your data or documents.</li>
                  <li>Opt out of non-essential emails and cookies.</li>
                </ul>

                <h2 className="text-2xl font-semibold mt-8 mb-4">Security</h2>
                <p className="mb-6">
                  We implement industry-standard security measures (SSL, encryption, access controls) to protect your data. However, no method of transmission over the internet is 100% secure.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4">Children's Privacy</h2>
                <p className="mb-6">
                  Our Services are not intended for individuals under the age of 13. We do not knowingly collect data from children.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4">Changes to This Policy</h2>
                <p className="mb-6">
                  We may update this Privacy Policy from time to time. The latest version will always be available at <a href="https://legalish.site/privacy" className="text-primary hover:underline">https://legalish.site/privacy</a> with a revised effective date.
                </p>

                <h2 className="text-2xl font-semibold mt-8 mb-4">Contact Us</h2>
                <p className="mb-6">
                  For questions or requests regarding this Privacy Policy, contact us at:
                  <br />
                  📧 <a href="mailto:jellaharshith8521@gmail.com" className="text-primary hover:underline">jellaharshith8521@gmail.com</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}